import {
  ArrowRight,
  CheckCircle,
  Clock,
  Info,
  SquaresFour,
  Wallet,
  Warning,
  WarningCircle,
} from "@phosphor-icons/react";
import {
  BucketAmountSource,
  BudgetBucketView,
} from "screens/investment/types/budget-bucket";

interface BudgetBucketCardProps {
  bucket: BudgetBucketView;
  onClick: () => void;
  onToggleApproval: () => void;
  /** Open the "Budget detailverdeling" designer for this pot. */
  onSplit?: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/**
 * Compact card for one strategic budget bucket. Click body → drawer with
 * details + audit log. Click the approval badge → toggle approval (writes
 * audit fields server-side).
 */
export const BudgetBucketCard = ({
  bucket,
  onClick,
  onToggleApproval,
  onSplit,
}: BudgetBucketCardProps) => {
  const isOverflow = !!bucket.isOverflow;
  const isOverspent = bucket.remainingAmount < 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex flex-col gap-3 rounded-lg border p-4 text-left transition-all ${
        isOverflow
          ? "border-amber-200 bg-amber-50/60 hover:border-amber-300"
          : "border-neutral-200 bg-white hover:border-moss-primary/40 hover:shadow-sm"
      }`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          {isOverflow ? (
            <WarningCircle
              weight="duotone"
              className="h-5 w-5 shrink-0 text-amber-600 mt-0.5"
            />
          ) : (
            <Wallet
              weight="duotone"
              className="h-5 w-5 shrink-0 text-moss-primary mt-0.5"
            />
          )}
          <div className="min-w-0">
            <h3
              className={`break-words text-sm font-semibold leading-tight ${
                isOverflow ? "text-amber-900" : "text-neutral-primary"
              }`}
            >
              {bucket.name}
            </h3>
            {bucket.year && (
              <p className="text-xs text-neutral-secondary-mid">
                {bucket.year}
              </p>
            )}
            {isOverflow && (
              <p className="text-[10px] text-amber-700">Verwachte overflow · alleen-lezen</p>
            )}
          </div>
        </div>
        {isOverflow ? (
          <span className="flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
            <Warning weight="fill" className="h-3 w-3" />
            Auto-berekend
          </span>
        ) : (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onToggleApproval();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onToggleApproval();
              }
            }}
            className={`flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
              bucket.isApproved
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
            }`}
            title={
              bucket.isApproved
                ? "Klik om goedkeuring in te trekken"
                : "Klik om goed te keuren"
            }
          >
            {bucket.isApproved ? (
              <CheckCircle weight="fill" className="h-3 w-3" />
            ) : (
              <Clock weight="fill" className="h-3 w-3" />
            )}
            {bucket.isApproved ? "Goedgekeurd" : "Niet goedgekeurd"}
          </span>
        )}
      </header>

      {isOverflow ? (
        <>
          <div className="flex items-baseline justify-between">
            <span className="text-lg font-semibold text-amber-900">
              {formatCurrency(bucket.usedAmount)}
            </span>
            <span className="text-xs text-amber-700">
              verwachte overflow
            </span>
          </div>
          <div className="text-[10px] text-amber-700">
            Som van toewijzingen die het strategisch budget van hun lijn
            overschrijden, voorbij de vrije ruimte van die lijn. Wordt pas
            effectieve overflow zodra alle andere lijnen 100% benut zijn —
            het strategisch budget is nu nog niet overschreden.
          </div>
        </>
      ) : (
        <>
          {/* Stage 1 — Strategisch budget (top headline).
              The cap is the anchor for everything that follows. */}
          <div className="flex items-baseline justify-between">
            <span className="text-lg font-semibold text-neutral-primary">
              {formatCurrency(bucket.totalAmount)}
            </span>
            <span className="text-[10px] uppercase tracking-wide text-neutral-secondary-mid">
              strategisch budget
            </span>
          </div>

          {/* Zeggen dat dit bedrag niet met de hand bijgehouden wordt: anders
              lijkt het bewerkbaar en typt iemand er een cijfer in dat bij de
              volgende lading weer overschreven wordt. */}
          {bucket.amountSource ===
            BucketAmountSource.ACQUISITION_RENOVATION_BUDGET && (
            <span
              className="text-neutral-secondary-mid text-[10px] leading-tight"
              title='Volgt gebouw → Financieel → "+ Renovatiebudget (incl. btw) bij aankoop"'
            >
              Volgt het renovatiebudget bij aankoop van dit gebouw
            </span>
          )}

          {/* Stage 2 — Reeds gedetailleerd vs nog te detailleren.
              Distribution view: how much of the strategic budget has been
              broken down into investment lines. Distinct from Stage 3
              (commitment) — drawn money isn't necessarily committed yet. */}
          {(() => {
            const detailedPct =
              bucket.totalAmount > 0
                ? Math.min(
                    100,
                    Math.max(
                      0,
                      (bucket.usedAmount / bucket.totalAmount) * 100,
                    ),
                  )
                : 0;
            return (
              <div className="flex flex-col gap-1 border-t border-neutral-100 pt-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-medium text-neutral-primary">
                    Reeds gedetailleerd in {bucket.investmentItemCount}{" "}
                    investeringslij{bucket.investmentItemCount === 1 ? "n" : "nen"}
                  </span>
                  <span className="text-xs font-semibold text-neutral-primary">
                    {formatCurrency(bucket.usedAmount)} ({Math.round(detailedPct)}%)
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
                  <div
                    className={`h-full rounded-full transition-all ${
                      isOverspent
                        ? "bg-red-500"
                        : detailedPct >= 80
                          ? "bg-amber-500"
                          : "bg-moss-primary"
                    }`}
                    style={{ width: `${isOverspent ? 100 : detailedPct}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] text-neutral-secondary-mid">
                  <span>{bucket.transactionCount} mut.</span>
                  <span>
                    nog te detailleren:{" "}
                    <span className="font-medium text-neutral-primary">
                      {formatCurrency(Math.max(0, bucket.remainingAmount))}
                    </span>
                  </span>
                </div>
              </div>
            );
          })()}

          {/* Stage 3 — Toegewezen aan projectonderdelen (commitment view).
              Designed around three audiences:
                board  → headline ratio + absolute overshoot in plain Dutch
                PM     → top overrun lines BY NAME so triage is one click away
                UX     → single bar where strategic budget = 100% denominator,
                         color-coded amber/red only on real risk; "verwachte
                         overflow" is demoted to a small italic projection,
                         not a red headline (its math is a worst-case fiction
                         that assumes every line reaches 100% utilisation —
                         see board feedback). */}
          {(() => {
            const withinEstimate = Math.max(
              0,
              bucket.committedWithinEstimateAmount || 0,
            );
            const overrun = Math.max(0, bucket.estimateOverrunAmount || 0);
            const totalCommitted = withinEstimate + overrun;
            const cap = bucket.totalAmount;
            // Current actual overshoot — the simple "I have X committed
            // against Y budget" view. Free in-line slack already absorbs the
            // gap between drawn and committed, so this is much smaller than
            // the worst-case "verwachte overflow".
            const currentOvershoot = Math.max(0, totalCommitted - cap);
            const committedRatioPct =
              cap > 0 ? Math.round((totalCommitted / cap) * 100) : 0;
            // Worst-case projection: if every line reaches 100% utilisation.
            const overrunCovered = Math.min(
              overrun,
              Math.max(0, bucket.remainingAmount),
            );
            const worstCase = Math.max(0, overrun - overrunCovered);

            const hasRisk = currentOvershoot > 0 || worstCase > 0;
            const hasActualBreach = currentOvershoot > 0;

            // Bar uses cap as 100%. Green/amber portion = vastgelegd, capped
            // at 100%. Red sliver to the right of the cap = currentOvershoot
            // actually breaching the cap right now (not the worst-case).
            const fillPct =
              cap > 0
                ? Math.min(100, (totalCommitted / cap) * 100)
                : 0;
            const overshootPct =
              cap > 0 && currentOvershoot > 0
                ? Math.min(20, (currentOvershoot / cap) * 100)
                : 0;
            const fillColor = hasActualBreach
              ? "bg-red-500"
              : hasRisk
                ? "bg-amber-500"
                : fillPct >= 80
                  ? "bg-amber-500"
                  : "bg-moss-primary";

            const ratioPillClasses = hasActualBreach
              ? "bg-red-100 text-red-700"
              : hasRisk
                ? "bg-amber-100 text-amber-800"
                : "bg-moss-primary/10 text-moss-primary";

            return (
              <div className="flex flex-col gap-1.5 border-t border-neutral-100 pt-2">
                {/* HERO — board-friendly ratio + absolute overshoot */}
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-neutral-primary">
                      Toegewezen aan projectonderdelen
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ratioPillClasses}`}
                    >
                      {committedRatioPct}%
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span
                      className={`text-lg font-semibold ${
                        hasActualBreach
                          ? "text-red-700"
                          : "text-neutral-primary"
                      }`}
                    >
                      {formatCurrency(totalCommitted)}
                    </span>
                    <span className="text-xs text-neutral-secondary-mid">
                      van {formatCurrency(cap)} strategisch
                    </span>
                  </div>
                </div>

                {/* SINGLE BAR — strategic budget cap = 100%.
                    A small red overshoot extends to the right of 100% only
                    when the cap is actually breached. */}
                <div className="relative">
                  <div className="flex h-2 w-full overflow-hidden rounded-full bg-neutral-100">
                    <div
                      className={`h-full transition-all ${fillColor}`}
                      style={{ width: `${fillPct}%` }}
                    />
                    {overshootPct > 0 && (
                      <div
                        className="h-full bg-red-600 transition-all"
                        style={{ width: `${overshootPct}%` }}
                      />
                    )}
                  </div>
                </div>

                {/* RISK CALLOUT — adapts to the situation
                    • actual breach (currentOvershoot > 0) → red headline
                      "+ €X boven strategisch budget"
                    • only worst-case risk → amber soft "opvangbaar"
                    • clean → no callout at all */}
                {hasActualBreach && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5">
                    <div className="flex items-baseline justify-between">
                      <span className="flex items-center gap-1 text-[11px] font-medium text-red-700">
                        <Warning weight="fill" className="h-3 w-3" />
                        Boven strategisch budget
                      </span>
                      <span className="text-[11px] font-semibold text-red-700">
                        + {formatCurrency(currentOvershoot)}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[10px] text-red-700/90">
                      {formatCurrency(totalCommitted)} toegewezen tegenover{" "}
                      {formatCurrency(cap)} budget. Verhoog het strategisch
                      budget of corrigeer een raming op de overschrijdende
                      lijnen hieronder.
                    </div>
                  </div>
                )}

                {/* TOP OVERRUN LINES — PM triage.
                    Names + numbers per offending line so the user knows
                    exactly which to open. */}
                {bucket.topOverrunLines && bucket.topOverrunLines.length > 0 && (
                  <div className="flex flex-col gap-1.5 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1.5">
                    <div className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-neutral-secondary-mid">
                      Meer gebruikt op projectonderdelen dan werd ingeschat
                      in de detailverdeling van het strategisch budget
                    </div>
                    {bucket.topOverrunLines.map((line) => {
                      const est = Number(line.estimatedCostExclVat) || 0;
                      const allocated = Number(line.allocatedBudget) || 0;
                      const lineOverrun =
                        Number(line.overrunAmount) || 0;
                      const overrunRatio =
                        est > 0 ? Math.round((allocated / est) * 100) : 0;
                      return (
                        <div
                          key={line.id}
                          className="flex flex-col gap-0.5 text-[10px]"
                        >
                          <span className="break-words font-medium text-neutral-primary">
                            {line.title}
                          </span>
                          <span className="text-neutral-secondary-mid">
                            raming{" "}
                            <span className="text-neutral-primary">
                              {formatCurrency(est)}
                            </span>{" "}
                            → toegewezen{" "}
                            <span className="text-neutral-primary">
                              {formatCurrency(allocated)}
                            </span>{" "}
                            <span className="font-semibold text-amber-700">
                              +{formatCurrency(lineOverrun)}
                            </span>
                            {est > 0 && (
                              <span className="ml-1 text-neutral-secondary-mid">
                                ({overrunRatio}%)
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* WORST-CASE PROJECTIE — prominent amber callout.
                    Features an arrow → that visually ties this number to
                    the synthetic "Verwachte overflow" card to its right
                    in the bucket grid, so PMs see exactly where the
                    money would land if the projection materialises.
                    The formula stays in the tooltip on the info icon. */}
                {worstCase > 0 && (
                  <div className="rounded-md border-l-4 border-amber-500 bg-amber-50 px-2.5 py-2">
                    {/* Row 1 — label + info icon, full width, never truncated */}
                    <div className="flex items-center gap-1.5">
                      <Warning
                        weight="fill"
                        className="h-4 w-4 shrink-0 text-amber-600"
                      />
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                        Dreigende overflow
                      </span>
                      <span
                        className="inline-flex shrink-0"
                        title={
                          `Berekening: ${formatCurrency(bucket.usedAmount)} ` +
                          `gedetailleerd + ${formatCurrency(overrun)} ` +
                          `lijn-overschrijding − ${formatCurrency(cap)} ` +
                          `strategisch budget = ${formatCurrency(worstCase)} ` +
                          `overflow.\n\n` +
                          `Wordt effectief wanneer alle investeringslijnen ` +
                          `voor 100% benut worden — wat op renovatie-` +
                          `projecten in praktijk vaak gebeurt.`
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Info
                          weight="regular"
                          className="h-3.5 w-3.5 cursor-help text-amber-700/70 hover:text-amber-700"
                        />
                      </span>
                    </div>
                    {/* Row 2 — amount → destination chip, wraps if needed */}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="text-base font-bold text-amber-800">
                        {formatCurrency(worstCase)}
                      </span>
                      <ArrowRight
                        weight="bold"
                        className="h-4 w-4 shrink-0 text-amber-700"
                      />
                      <span className="rounded-md border border-amber-400 bg-amber-200/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                        Verwachte overflow
                      </span>
                    </div>
                    <div className="mt-1 text-[10px] text-amber-700/90">
                      Indien alle lijnen volledig benut worden, verschuift
                      dit bedrag naar de Verwachte overflow. Verhoog het
                      strategisch budget of corrigeer de overschrijdende
                      ramingen om dit te voorkomen.
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {isOverspent && (
            <div className="flex items-center gap-1 text-[10px] text-red-700">
              <Warning weight="fill" className="h-3 w-3" />
              Strategisch budget overschreden met{" "}
              {formatCurrency(Math.abs(bucket.remainingAmount))}
            </div>
          )}

          {/* Primary CTA — fast split designer. Disabled when there's
              nothing left to distribute to keep the affordance honest. */}
          {onSplit && (
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                if (bucket.remainingAmount > 0) onSplit();
              }}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  bucket.remainingAmount > 0
                ) {
                  e.stopPropagation();
                  onSplit();
                }
              }}
              className={`mt-2 flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-all ${
                bucket.remainingAmount > 0
                  ? "border-moss-primary bg-moss-primary text-white hover:bg-moss-primary/90 cursor-pointer shadow-sm"
                  : "border-neutral-200 bg-neutral-50 text-neutral-400 cursor-not-allowed"
              }`}
              title={
                bucket.remainingAmount > 0
                  ? "Snel verdelen over labels (drag & drop)"
                  : "Geen budget meer over om te verdelen"
              }
            >
              <SquaresFour className="h-3.5 w-3.5" weight="fill" />
              Verdeel budget
            </div>
          )}
        </>
      )}
    </button>
  );
};
